import Http from '@/utils/http';
import {
    IPaperSubmitStatusData,
    AppResponse,
    IPaperData,
    ISubmitPaperParams,
    PaperAnalysisDataType,
    PracticeAnalysisDataType,
    IQuestionListItem,
    ISubmitClozeAnswer,
    type IQuestionListItemUserAnswer,
} from '@/types';

// 用户创建试卷
export const createPaper = (params: {
    paperId: number;
    sourceFromType: number; // ：数据来源类型: 0老系统(默认) 100533963会计 100533962课程 100533961公务员考试
}): Promise<AppResponse<IPaperData>> => Http.post('/minerva/api/v1/front/create-paper', params);

// 试卷/练习 继续做/重做
export const redoPaper = (params: {
    needReviewInfo: number; // 是否需要批阅信息 0 不需要 1需要
    paperDataLogId: number;
    sourceFromType: number; // ：数据来源类型: 0老系统(默认) 100533963会计 100533962课程 100533961公务员考试
    businessParam?: {};
}): Promise<AppResponse<IPaperData>> => Http.post('/minerva/api/v1/front/redo-paper', { ...params, openEnergyToEquity: 1 });

// 试卷/练习 交题
export const submitQuestion = (params: {
    paperId: number; // 试卷id
    paperDataId: number; // 学生试卷唯一记录id
    paperDataLogId: number; // 学生每次做试卷记录id
    questionId: number; // 题目id
    costTime: number; // 花费时间
    userAnswer: string | number; // 用户答案
    userClozeAnswers?: ISubmitClozeAnswer[]; // 完型题答案
    userAnswerImagesList?: string[]; // 用户答案上传的图片
    excelAnswer?: string; // excel答题
    practiceMode?: number; // 0练习 1背题
}): Promise<AppResponse<IQuestionListItemUserAnswer>> => Http.post('/minerva/api/v1/front/submit-question', params);

// 试卷/练习 交卷
export const submitPaper = (params: ISubmitPaperParams): Promise<AppResponse<any>> =>
    Http.post('/minerva/api/v1/front/submit-paper', {
        ...params,
        userAnswerList: params.userAnswerList.map(item => ({
            ...item,
            userAnswerImageList: item.userAnswerImagesList || [],
        })),
    });

// 通过paperDataLogId查询学生一张做试卷状态
export const getPaperDataLogStatus = (params: {
    paperDataLogId: number; // 试卷id
}): Promise<AppResponse<IPaperSubmitStatusData>> => Http.get('/minerva/api/v1/front/student/paper-data-log/status', params);

// 练习解析（全部，错题）
export const getPracticeAnalysis = (params: {
    paperDataLogId: number; // 试卷id
    analysisType: number; // 获取解析的类型 1全部 2 错题
}): Promise<AppResponse<PracticeAnalysisDataType>> =>
    Http.post('/minerva/api/v1/front/exercise/analysis', { ...params, openEnergyToEquity: 1 });

// 试卷解析（全部，错题）
export const getPaperAnalysis = (params: {
    paperDataLogId: number; // 试卷id
    analysisType: number; // 获取解析的类型 1全部 2 错题
}): Promise<AppResponse<PaperAnalysisDataType>> => Http.post('/minerva/api/v1/front/paper/analysis', { ...params, openEnergyToEquity: 1 });

// 批量查询题目信息
export const getQuestionList = (params: { questionIdList: number[] }): Promise<AppResponse<IQuestionListItem[]>> =>
    Http.post('/minerva/api/v1/front/question/list', params);

// 一题题做，提交单题
export const submitSingleQuestion = (params: {
    questionId: number; // 题目id
    userAnswer: string | number; // 用户答案
    userAnswerImagesList?: string[]; // 用户答案上传的图片
    excelAnswer?: string; // excel答案
    userClozeAnswers?: { key: string; rightAnswer: string; userAnswer: string }[];
    businessParam?: {};
}): Promise<AppResponse<IQuestionListItem>> => Http.post('/minerva/api/v1/front/submit-single-question', params);

// 申论判断是否有权益批改
export const getEquityCheck = (params: {
    aiTutorConfigId: number;
    equityType: number; // 权益类型，3：智能批改，4：人工批改
    projectId: number;
}): Promise<
    AppResponse<{
        total: number; // 剩余权益次数
        usable: boolean; // 权益是否足够，true：足够，false：不够
        tips: string; // 批改提示，本次批改消耗的权益值、充值提示
        noTimesTips: string; // 聊天框提示文案(app)
        leftoverTips: 'mixed'; // 剩余能量提示
    }>
> => Http.get('/aitutor/api/v1/front/equity/check', params);

// 发送ai请求
export const sendAiQuestionCorrect = (params: {
    itemId: number; // 题目id
    paperDataLogId: number; // 试卷记录id
    aiTutorConfigId: number; // 类型：Number  必有字段  备注：ai tutor配置id
    sourceType: number; // 类型：String  可有字段  备注：平台来源：100536074 学习平台；100536073 中台；100536072 教务 100536075 公考 默认 学习平台
    userAnswer: string; // 类型：String  必有字段  备注：学员答案
}) => Http.post('/aitutor/api/v1/front/question/correct-ai/correct', { ...params, openEnergyToEquity: 1 });

// 发送ai请求
export const sendCpaAiQuestionCorrect = (params: {
    itemId: number; // 题目id
    paperDataLogId: number; // 试卷记录id
    aiTutorConfigId: number; // 类型：Number  必有字段  备注：ai tutor配置id
    sourceType: number; // 类型：String  可有字段  备注：平台来源：100536074 学习平台；100536073 中台；100536072 教务 100536075 公考 默认 学习平台
    userAnswer: string; // 类型：String  必有字段  备注：学员答案
    excelAnswer?: string; // 类型：String  必有字段  备注：学员答案
}) => Http.post('/aitutor/api/v1/front/question/correct-ai/cpa', { ...params, openEnergyToEquity: 1 });

// 获取ai申论 题目的批改状态 内容
export const getAiQuestionStatus = (params: {
    paperDataLogId: number; //	试卷记录id
    itemId: number; //	题目id
}) => Http.get('/aitutor/api/v1/front/question/correct-ai/status', params);

// 获取AI 结果 CPA 批改
export const getAiCPAQuestionStatus = (params: {
    paperDataLogId: number; //	试卷记录id
    itemId: number; //	题目id
}) => Http.get('/aitutor/api/v1/front/question/correct-ai/cpa/status', params);

// 查询 支不支持 luca
export const isSupportLucaTask = (params: {
    paperId?: number;
    itemId?: number;
    projectId: number;
}): Promise<AppResponse<{ open: boolean }>> => Http.post('/athena/api/v1/entrance/switch/status', { ...params, sceneFlag: 40 }); // 场景id：20-luca试卷；40-题库试卷

// 获取 userid
export const getUserIdTask = () => Http.get('/passport/api/v3/user/get-user-info-by-token');

// 获取最大金额
export const getMaxMoneyTask = (projectId: number) => Http.get('/minerva/api/v1/front/homepage/student/project/max-amount', { projectId });

// 【错题】取消错题
export const cancelWrongQuestion = (params: {
    questionId: number;
    questionIds?: number[];
    businessParam: {};
}): Promise<AppResponse<boolean>> => Http.post('/minerva/api/v1/front/question-wrong/cancel', params);

// 根据模考id查询学员做试卷状态
export const getSimulationExamRecord = (params: {
    simulationExamId: number;
}): Promise<
    AppResponse<{
        paperDataLogId: number;
        paperId: number;
        paperSubmitStatus: number; // 当前做试卷状态 0:未交卷 1：已交卷
        simulationCreate: Boolean; // paperDataLogId是否模考创建的，true：是，false：在其他地方创建
    }>
> => Http.get('/minerva/api/v1/front/simulation-exam/status', params);

// 模考/ 继续做/重做
export const simulationRedoPaper = (params: {
    paperDataLogId: number;
    sourceFromType: number; // ：数据来源类型: 0老系统(默认) 100533963会计 100533962课程 100533961公务员考试
    simulationExamId: number;
}): Promise<AppResponse<IPaperData>> => Http.post('/minerva/api/v1/front/simulation-exam/simulation-exam-paper', params);

// 获取具体问题解析
export const getKnowledgeAnalysisListTask = (params: {
    categoryId: number; // 知识点id
    projectId: number; // 项目id
    subjectId: number; // 科目id
    itemIdList: number[]; // 问题列表
}): Promise<AppResponse<any>> => Http.post(`/minerva/api/v1/front/module/category/done/history`, params);

// 清除题目知识点做题记录
export const clearQuestionRecord = (params: { itemCategoryId: number }): Promise<AppResponse<null>> =>
    Http.post('/minerva/api/v1/front/module/category/record/clear ', params);

// tiku3.0 获取考点解析题目数据
export const getExamPointQuestionsAnalysis = (params: {
    knowledgePointId: number; // 考点id
    projectId: number; // 项目id
    subjectId: number; // 科目id
}): Promise<AppResponse<any>> => Http.post('/minerva/api/v1/front/predict-score/knowledge-point/analysis ', params);

// 学员发起付费批改
export const askPayCorrectPaper = (params: {
    paperDataLogId: number; // 试卷记录id
    projectId: number; // 项目id
    subjectId: number; // 科目id
}): Promise<AppResponse<any>> => Http.post('/minerva/api/v1/front/pay-correct', params);

// 查询用户权益
export const getUserEquity = (params: { projectId: number }): Promise<AppResponse<any>> =>
    Http.get('/aitutor/api/v1/front/equity/student/equity/info', params);
